import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { interconnectionConverter, offerConverter } from '@seu-converter';
import {
  OfferFormService,
  RequestService,
  StoreService,
  ToastService,
} from '@seu-services';
import { SEU_API_ENDPOINTS, SeuInterconnection, SeuOfferApi } from '@seu-types';
import { last } from 'lodash-es';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InterconnectionService {
  private readonly toastService = inject(ToastService);
  private readonly offerFormService = inject(OfferFormService);
  private readonly store = inject(StoreService);
  private readonly requestService = inject(RequestService);
  private readonly translateService = inject(TranslateService);

  public async createInterconnection(
    offerId: string
  ): Promise<SeuInterconnection | null> {
    const endpoint = SEU_API_ENDPOINTS.interconnection.replace(
      ':offerId',
      offerId
    );

    const data = {
      name: this.translateService.instant('global.untitled-interconnection'),
    };

    const responseData = await firstValueFrom(
      this.requestService.post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, data)
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      return last(updatedOffer.interconnections) ?? null;
    }

    return null;
  }

  public async duplicateInterconnection(
    offerId: string,
    interconnection: SeuInterconnection
  ): Promise<SeuInterconnection | null> {
    const newName = `${this.translateService.instant('global.copy-from')} ${interconnection.name}`;
    const endpoint = SEU_API_ENDPOINTS.interconnectionDuplicate
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnection.id)
      .concat(`?name=${encodeURIComponent(newName)}`);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, {
          name: `${this.translateService.instant('global.copy-from')} ${interconnection.name}`,
        })
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'interconnection.toasts.duplicate-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      return last(updatedOffer.interconnections) ?? null;
    }

    return null;
  }

  public async deleteInterconnection(
    offerId: string,
    interconnectionId: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.interconnectionDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId);

    const responseData = await firstValueFrom(
      this.requestService
        .delete<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'interconnection.toasts.delete-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'interconnection.toasts.delete-success',
      // });
    }
  }

  public async updateInterconnection(
    offerId: string,
    interconnectionId: string,
    interconnection: SeuInterconnection
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.interconnectionDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId);

    const data = interconnectionConverter.toApi(interconnection);

    const responseData = await firstValueFrom(
      this.requestService.put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, data)
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
    }
  }
}
